import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { WordSuggestionsAdmin } from '@/components/WordSuggestionsAdmin';
import { ClueSuggestionsAdmin } from '@/components/ClueSuggestionsAdmin';
import { UserManagementAdmin } from '@/components/UserManagementAdmin';
import { WordImportAdmin } from '@/components/WordImportAdmin';
import { ActivityLogAdmin } from '@/components/ActivityLogAdmin';
import { BackupAdmin } from '@/components/BackupAdmin';
import { ClueImportAdmin } from '@/components/ClueImportAdmin';
import { DatabaseManagementAdmin } from '@/components/DatabaseManagementAdmin';
import { AvatarReviewAdmin } from '@/components/AvatarReviewAdmin';
import { SearchHistoryAdmin } from '@/components/SearchHistoryAdmin';
import { DocsAdmin } from '@/components/DocsAdmin';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Shield, Upload, CheckCircle, Loader2, Users, Database, Activity, Link as LinkIcon, Download, Settings, Image, History, FileText } from 'lucide-react';

const LANGUAGE_OPTIONS = [
  { value: 1, label: '🇸🇪 Svenska' },
  { value: 2, label: '🇬🇧 Engelska' },
  { value: 4, label: '🇩🇪 Tyska' },
  { value: 8, label: '🇫🇷 Franska' },
  { value: 16, label: '🇪🇸 Spanska' },
];

interface ImportResult {
  imported: number;
  skipped: number;
  languageUpdated: number;
  errors: string[];
}

export default function Admin() {
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading, session, user } = useAuth();
  const [wordList, setWordList] = useState('');
  const [wordLanguage, setWordLanguage] = useState(1); // Default Swedish
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Wait for both auth loading AND user data to be fetched
  const isLoading = authLoading || (session && !user);

  useEffect(() => {
    if (!isLoading && !isAdmin) {
      navigate('/');
    }
  }, [isLoading, isAdmin, navigate]);

  const parseWordList = (text: string): string[] => {
    // Split by newlines or commas, trim whitespace, filter empty strings
    return text
      .split(/[\n,]+/)
      .map(word => word.trim().toUpperCase())
      .filter(word => word.length > 0 && word.length <= 30)
      .filter(word => /^[A-ZÅÄÖ]+$/.test(word));
  };

  const handleImportWords = async () => {
    const words = parseWordList(wordList);
    
    if (words.length === 0) {
      toast.error('Inga giltiga ord hittades. Ord får endast innehålla bokstäver A-Ö och vara max 30 tecken.');
      return;
    }

    if (words.length > 100) {
      toast.error('Max 100 ord kan importeras åt gången.');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const result: ImportResult = { imported: 0, skipped: 0, languageUpdated: 0, errors: [] };

      for (const word of words) {
        // Check if word already exists
        const { data: existing } = await supabase
          .from('words')
          .select('id, language')
          .eq('word_upper', word)
          .maybeSingle();

        if (existing) {
          // Check if we need to add the language
          const currentLang = existing.language || 0;
          if ((currentLang & wordLanguage) === 0) {
            // Add language to bitmask
            const { error: updateError } = await supabase
              .from('words')
              .update({ language: currentLang | wordLanguage })
              .eq('id', existing.id);
            
            if (!updateError) {
              result.languageUpdated++;
            }
          } else {
            result.skipped++;
          }
          continue;
        }

        // Insert the word with selected language
        const { error } = await supabase
          .from('words')
          .insert({
            word: word.toLowerCase(),
            word_upper: word,
            length: word.length,
            language: wordLanguage
          });

        if (error) {
          result.errors.push(`${word}: ${error.message}`);
        } else {
          result.imported++;
        }
      }

      setImportResult(result);
      
      if (result.imported > 0 || result.languageUpdated > 0) {
        const messages = [];
        if (result.imported > 0) messages.push(`${result.imported} ord importerade`);
        if (result.languageUpdated > 0) messages.push(`${result.languageUpdated} ord uppdaterade med nytt språk`);
        toast.success(messages.join(', ') + '!');
        setWordList('');
      } else if (result.skipped === words.length) {
        toast.info('Alla ord finns redan i databasen med valt språk.');
      } else if (result.errors.length > 0) {
        toast.error(`Fel vid import av ${result.errors.length} ord.`);
      }
    } catch (error) {
      toast.error('Ett fel uppstod vid importen.');
      console.error('Import error:', error);
    } finally {
      setImporting(false);
    }
  };

  const wordCount = parseWordList(wordList).length;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold">Administration</h1>
            <p className="text-muted-foreground text-sm">Hantera ord och innehåll</p>
          </div>
        </div>

        <Tabs defaultValue="activity" className="space-y-6">
          <TabsList className="grid w-full grid-cols-11">
            <TabsTrigger value="activity" className="gap-2">
              <Activity className="h-4 w-4" />
              <span className="hidden sm:inline">Aktivitet</span>
            </TabsTrigger>
            <TabsTrigger value="searches" className="gap-2">
              <History className="h-4 w-4" />
              <span className="hidden sm:inline">Sökningar</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Användare</span>
            </TabsTrigger>
            <TabsTrigger value="avatars" className="gap-2">
              <Image className="h-4 w-4" />
              <span className="hidden sm:inline">Bilder</span>
            </TabsTrigger>
            <TabsTrigger value="suggestions" className="gap-2">
              <CheckCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Ordförslag</span>
            </TabsTrigger>
            <TabsTrigger value="clue-suggestions" className="gap-2">
              <LinkIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Ledtrådar</span>
            </TabsTrigger>
            <TabsTrigger value="database" className="gap-2">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Hantera</span>
            </TabsTrigger>
            <TabsTrigger value="old-import" className="gap-2">
              <Database className="h-4 w-4" />
              <span className="hidden sm:inline">Gamla DB</span>
            </TabsTrigger>
            <TabsTrigger value="import" className="gap-2">
              <Upload className="h-4 w-4" />
              <span className="hidden sm:inline">Import</span>
            </TabsTrigger>
            <TabsTrigger value="backup" className="gap-2">
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Backup</span>
            </TabsTrigger>
            <TabsTrigger value="docs" className="gap-2">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Docs</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="activity">
            <ActivityLogAdmin />
          </TabsContent>

          <TabsContent value="searches">
            <SearchHistoryAdmin />
          </TabsContent>

          <TabsContent value="users">
            <UserManagementAdmin />
          </TabsContent>

          <TabsContent value="avatars">
            <AvatarReviewAdmin />
          </TabsContent>

          <TabsContent value="suggestions">
            <WordSuggestionsAdmin />
          </TabsContent>

          <TabsContent value="clue-suggestions">
            <ClueSuggestionsAdmin />
          </TabsContent>

          <TabsContent value="database">
            <DatabaseManagementAdmin />
          </TabsContent>

          <TabsContent value="old-import">
            <WordImportAdmin />
          </TabsContent>

          <TabsContent value="import" className="space-y-6">
            <ClueImportAdmin />
            
            <Card>
              <CardHeader>
                <CardTitle className="font-display">Importera ordlista</CardTitle>
                <CardDescription>
                  Klistra in ord separerade med radbrytning eller kommatecken. Max 100 ord åt gången.
                  Endast bokstäver A-Ö tillåts, max 30 tecken per ord.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="wordLanguage">Språk</Label>
                  <Select
                    value={String(wordLanguage)}
                    onValueChange={(value) => setWordLanguage(Number(value))}
                  >
                    <SelectTrigger id="wordLanguage" className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={String(opt.value)}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="wordList">Ordlista</Label>
                  <Textarea
                    id="wordList"
                    value={wordList}
                    onChange={(e) => setWordList(e.target.value)}
                    placeholder="KORSORD&#10;LEXIKON&#10;SYNONYM&#10;&#10;eller:&#10;&#10;KORSORD, LEXIKON, SYNONYM"
                    rows={10}
                    className="font-mono"
                  />
                  <p className="text-sm text-muted-foreground">
                    {wordCount} giltiga ord identifierade
                  </p>
                </div>

                <Button 
                  onClick={handleImportWords} 
                  disabled={importing || wordCount === 0}
                  className="gap-2"
                >
                  {importing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Importerar...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      Importera {wordCount} ord
                    </>
                  )}
                </Button>

                {importResult && (
                  <div className="mt-4 p-4 rounded-lg bg-muted space-y-2">
                    <p className="font-medium">Resultat:</p>
                    <ul className="text-sm space-y-1">
                      <li className="text-green-600 dark:text-green-400">
                        ✓ {importResult.imported} ord importerade
                      </li>
                      {importResult.languageUpdated > 0 && (
                        <li className="text-blue-600 dark:text-blue-400">
                          + {importResult.languageUpdated} ord uppdaterade med nytt språk
                        </li>
                      )}
                      {importResult.skipped > 0 && (
                        <li className="text-muted-foreground">
                          ○ {importResult.skipped} ord fanns redan med valt språk
                        </li>
                      )}
                      {importResult.errors.length > 0 && (
                        <li className="text-destructive">
                          ✕ {importResult.errors.length} fel
                        </li>
                      )}
                    </ul>
                    {importResult.errors.length > 0 && (
                      <details className="text-sm">
                        <summary className="cursor-pointer text-muted-foreground">
                          Visa felmeddelanden
                        </summary>
                        <ul className="mt-2 space-y-1 text-destructive">
                          {importResult.errors.map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="backup">
            <BackupAdmin />
          </TabsContent>

          <TabsContent value="docs">
            <DocsAdmin />
          </TabsContent>
        </Tabs>
      </main>

      <Footer />
    </div>
  );
}
